import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useTabletMarks } from "../hooks/useTabletMarks";

// #3450 -- static i18n key tables, never t(`...${code}`) (feedback_no_dynamic_i18n_keys).
const REFUSAL_KEYS = {
  sheet_completed: "rideEditor.tabletMarks.refusals.sheet_completed",
  marks_confirmed_frozen: "rideEditor.tabletMarks.refusals.marks_confirmed_frozen",
  sheet_editing: "rideEditor.tabletMarks.refusals.sheet_editing",
};
const ERROR_KEYS = {
  sheet_changed_since_diff: "rideEditor.tabletMarks.errors.sheet_changed_since_diff",
  snapshot_changed_since_diff: "rideEditor.tabletMarks.errors.snapshot_changed_since_diff",
  no_tablet_marks: "rideEditor.tabletMarks.errors.no_tablet_marks",
  no_tablet_marks_difference: "rideEditor.tabletMarks.errors.no_tablet_marks_difference",
  concurrent_modification: "rideEditor.tabletMarks.errors.concurrent_modification",
  sheet_completed: "rideEditor.tabletMarks.errors.sheet_completed",
  marks_confirmed_frozen: "rideEditor.tabletMarks.errors.marks_confirmed_frozen",
  sheet_editing: "rideEditor.tabletMarks.errors.sheet_editing",
  validation_error: "rideEditor.tabletMarks.errors.validation_error",
};

// Score Admin's "Tablet marks" dialog (#3450): the per-movement and
// per-collective diff of a tablet's newest saved marks against the server's
// sheet, and the Apply that writes them (server refuses if the sheet or the
// snapshot changed since this diff was loaded, or the sheet is completed /
// confirmed). Mount only while open (state loads fresh on every open).
export default function TabletMarksDialog({ sheetId, onClose, onApplied }) {
  const { t } = useTranslation("scoreadmin");
  const { loading, error, data, apply, applying } = useTabletMarks(sheetId);
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [applyError, setApplyError] = useState(null);
  const [done, setDone] = useState(false);

  const candidates = data?.candidates ?? [];
  const candidate = candidates.find((c) => c.snapshotKey === picked) ?? candidates[0] ?? null;
  const refusal = data?.sheet?.refusal ?? null;
  const canApply =
    !!candidate && !refusal && !done && !!data?.sheet?.updatedAt && candidate.diff.changedCount > 0;

  const markText = (mark) => {
    if (!mark) return t("rideEditor.tabletMarks.none");
    const parts = [mark.score === null ? t("rideEditor.tabletMarks.blank") : String(mark.score)];
    if (mark.flagged) parts.push(t("rideEditor.tabletMarks.flagged"));
    if (mark.deductions?.length) {
      parts.push(t("rideEditor.tabletMarks.deductions", { count: mark.deductions.length }));
    }
    return parts.join(", ");
  };

  const rows = candidate
    ? [
        ...candidate.diff.movements.map((r) => ({
          id: `m${r.movementNumber}`,
          label: t("rideEditor.tabletMarks.movementRow", { number: r.movementNumber }),
          row: r,
        })),
        ...candidate.diff.collectives.map((r) => ({
          id: `c${r.key}`,
          label: t("rideEditor.tabletMarks.collectiveRow", { key: r.key }),
          row: r,
        })),
      ]
    : [];

  const confirm = async () => {
    setApplyError(null);
    try {
      const updated = await apply(candidate, note.trim());
      setDone(true);
      onApplied?.(updated);
    } catch (err) {
      setApplyError(err?.response?.data?.code);
    }
  };

  const stationLabel = (c) =>
    c.snapshotAt
      ? t("rideEditor.tabletMarks.stationLabel", { device: c.deviceId, at: c.snapshotAt })
      : t("rideEditor.tabletMarks.stationLabelNoTime", { device: c.deviceId });

  return (
    <Dialog open onClose={() => !applying && onClose()} maxWidth="md" fullWidth data-testid="tablet-marks-dialog">
      <DialogTitle>{t("rideEditor.tabletMarks.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("rideEditor.tabletMarks.intro")}</DialogContentText>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} data-testid="tablet-marks-loading">
            <CircularProgress size={18} />
            <Typography>{t("rideEditor.tabletMarks.loading")}</Typography>
          </Box>
        )}
        {error && <Alert severity="error">{t("rideEditor.tabletMarks.loadError")}</Alert>}
        {data && refusal && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="tablet-marks-refusal">
            {t(REFUSAL_KEYS[refusal] ?? "rideEditor.tabletMarks.errors.default")}
          </Alert>
        )}
        {data && candidates.length === 0 && (
          <Alert severity="info" data-testid="tablet-marks-none">
            {t("rideEditor.tabletMarks.noCandidates")}
          </Alert>
        )}
        {candidate && (
          <>
            {candidates.length > 1 && (
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>{t("rideEditor.tabletMarks.chooseTablet")}</FormLabel>
                <RadioGroup value={candidate.snapshotKey} onChange={(e) => setPicked(e.target.value)}>
                  {candidates.map((c) => (
                    <FormControlLabel
                      key={c.snapshotKey}
                      value={c.snapshotKey}
                      control={<Radio />}
                      label={stationLabel(c)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            {candidates.length === 1 && (
              <Typography sx={{ mb: 1 }}>{stationLabel(candidate)}</Typography>
            )}
            <Typography sx={{ mb: 1 }} data-testid="tablet-marks-summary">
              {t("rideEditor.tabletMarks.summary", { count: candidate.diff.changedCount })}
            </Typography>
            <Table size="small" data-testid="tablet-marks-table">
              <TableHead>
                <TableRow>
                  <TableCell>{t("rideEditor.tabletMarks.colRow")}</TableCell>
                  <TableCell>{t("rideEditor.tabletMarks.colServer")}</TableCell>
                  <TableCell>{t("rideEditor.tabletMarks.colTablet")}</TableCell>
                  <TableCell>{t("rideEditor.tabletMarks.colChange")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(({ id, label, row }) => (
                  <TableRow key={id} data-testid={`tablet-marks-row-${id}`} data-changed={row.changed ? "true" : "false"}>
                    <TableCell>{label}</TableCell>
                    <TableCell>{markText(row.server)}</TableCell>
                    <TableCell sx={row.changed ? { fontWeight: 600 } : undefined}>{markText(row.tablet)}</TableCell>
                    <TableCell>
                      {row.changed ? (
                        <Chip size="small" color="warning" label={t("rideEditor.tabletMarks.changed")} />
                      ) : (
                        t("rideEditor.tabletMarks.same")
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TextField
              sx={{ mt: 2 }}
              fullWidth
              size="small"
              label={t("rideEditor.tabletMarks.noteLabel")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={applying || done}
            />
          </>
        )}
        {applyError !== null && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="tablet-marks-error">
            {t(ERROR_KEYS[applyError] ?? "rideEditor.tabletMarks.errors.default")}
          </Alert>
        )}
        {done && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="tablet-marks-done">
            {t("rideEditor.tabletMarks.applied")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          {t("rideEditor.tabletMarks.close")}
        </Button>
        {!done && (
          <Button variant="contained" onClick={confirm} disabled={!canApply || applying} data-testid="tablet-marks-apply">
            {applying ? t("rideEditor.tabletMarks.applying") : t("rideEditor.tabletMarks.apply")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
